import axios from "axios";
import React, { useEffect, useState } from "react";
import { apiKey } from "../../../config/config";
import handleCSRF from "../../../config/CSRF";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

function AssignItemModifiers() {
  const user = JSON.parse(localStorage.getItem("rms-admin"));
  const { id } = useParams();
  const [modifiers, setModifiers] = useState([]);
  const [formData, setFormData] = useState({
    food_menu_id: id,
    user_id: user.user?.id,
    outlet_id: user.user?.outlet_id,
    modifiers: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const navigate = useNavigate();

  const handleSubmit = (e) => {
    setLoading(true);
    e.preventDefault();

    const updateMenuModifiers = () => {
      axios
        .put(`${apiKey}/api/rms/admin/foodMenuModifiers/${id}`, formData, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        })
        .then((res) => {
          toast.success("Success", {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          });
          navigate(-1);
        })
        .catch((err) => {
          toast.error("Error Occured", {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          });
        })
        .finally(() => {
          setLoading(false);
        });
    };
    handleCSRF(updateMenuModifiers);
  };

  useEffect(() => {
    const fetchModifiers = async () => {
      try {
        const foodMenuResponse = await axios.get(
          `${apiKey}/api/rms/admin/foodMenus/${id}`,
          {
            headers: {
              Authorization: `Bearer ${user?.token}`,
            },
          }
        );

        const allModifiersResponse = await axios.get(
          `${apiKey}/api/rms/admin/modifiers`,
          {
            headers: {
              Authorization: `Bearer ${user?.token}`,
            },
          }
        );

        const foodMenu = foodMenuResponse.data;
        const allModifiers = allModifiersResponse.data;

        setModifiers(allModifiers);
        const selectedModifiers = foodMenu.modifiers.map(
          (modifier) => modifier.modifier_id
        );
        setFormData((prev) => ({ ...prev, modifiers: selectedModifiers }));
      } catch (err) {
        setError(err.response?.data?.message || "Error loading modifiers");
      } finally {
        setPageLoading(false);
      }
    };

    handleCSRF(fetchModifiers);
  }, [user?.token, id]);

  const handleCheck = (e) => {
    const { id, checked } = e.target;

    if (id === "all") {
      // If "Select All" is checked or unchecked
      if (checked) {
        setFormData({
          ...formData,
          modifiers: modifiers.map((modifier) => modifier.id),
        });
      } else {
        setFormData({
          ...formData,
          modifiers: [],
        });
      }
    } else {
      // For individual checkboxes
      if (checked) {
        setFormData({
          ...formData,
          modifiers: [...formData.modifiers, parseInt(id)],
        });
      } else {
        setFormData({
          ...formData,
          modifiers: formData.modifiers.filter(
            (modifierId) => modifierId !== parseInt(id)
          ),
        });
      }
    }
  };

  const handleSelectAll = () => {
    setFormData((prev) => {
      const allModifierIds = modifiers.map((modifier) => modifier.id);
      const areAllSelected = allModifierIds.every((id) =>
        prev.modifiers.includes(id)
      );
      return { ...prev, modifiers: areAllSelected ? [] : allModifierIds };
    });
  };

  if (error) {
    return (
      <div
        className="flex items-center p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50"
        role="alert"
      >
        <svg
          className="flex-shrink-0 inline w-4 h-4 me-3"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
        </svg>
        <span className="sr-only">Info</span>
        <div>{error}</div>
      </div>
    );
  }

  if (pageLoading) {
    return (
      <div className="absolute top-0 left-0 w-full h-full grid place-items-center">
        <img src="/assets/gif/dash2.gif" alt="" />
      </div>
    );
  }

  return (
    <main>
      <div className="relative my-16 flex flex-col min-w-0 break-words w-full mb-6 shadow-lg bg-white rounded-lg p-4 border-0">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-d-bg-text text-2xl">Assign Food Menu Modifier</h1>
        </div>
        <div className="w-full">
          <form action="" className="w-full" onSubmit={handleSubmit}>
            <div className="w-full py-1 border-b">
              <div className="inline-flex items-center">
                <label
                  className="relative flex items-center px-3 rounded-full cursor-pointer"
                  htmlFor={"all"}
                >
                  <input
                    type="checkbox"
                    className="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-md border border-blue-gray-200 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity checked:border-d-bg checked:bg-d-bg checked:before:bg-d-bg hover:before:opacity-10"
                    id={"all"}
                    onChange={handleSelectAll}
                    checked={
                      modifiers.length > 0 &&
                      formData.modifiers.length === modifiers.length
                    }
                  />
                  <span className="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3.5 w-3.5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      stroke="currentColor"
                      strokeWidth="1"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                  </span>
                </label>
                Select All
              </div>
            </div>
            <div className="grid grid-cols-2">
              {modifiers.map((modifier) => (
                <div key={modifier.id} className="inline-flex items-center">
                  <label
                    className="relative flex items-center p-3 rounded-full cursor-pointer"
                    htmlFor={modifier.id}
                  >
                    <input
                      type="checkbox"
                      className="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-md border border-blue-gray-200 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity checked:border-d-bg checked:bg-d-bg checked:before:bg-d-bg hover:before:opacity-10"
                      id={modifier.id}
                      value={modifier.id}
                      onChange={handleCheck}
                      checked={formData.modifiers.includes(modifier.id)}
                    />
                    <span className="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3.5 w-3.5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        stroke="currentColor"
                        strokeWidth="1"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                    </span>
                  </label>
                  {modifier.name}
                </div>
              ))}
            </div>
            <span className="w-full bg-d-bg h-1 my-2 inline-block"></span>
            <div className="flex gap-2">
              <button
                className="bg-d-cat-btn text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center w-fit relative"
                type="submit"
              >
                {loading && (
                  <span className="absolute top-0 left-0 w-full h-full py-1 px-3 bg-black bg-opacity-50">
                    <img
                      className="w-4 mx-auto"
                      src="https://i.gifer.com/ZKZx.gif"
                      alt=""
                    />
                  </span>
                )}
                Submit
              </button>
              <button
                className="bg-d-cat-btn text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center w-fit"
                type="button"
                onClick={() => navigate(-1)}
              >
                Back
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}

export default AssignItemModifiers;
